// @flow

import type { ViewportCategory } from '@ta/common.state/meta';

// responsive breakpoints in px
export const BREAKPOINT_MOBILE = 320;
export const BREAKPOINT_TABLET = 768;
export const BREAKPOINT_DESKTOP = 1024;
export const BREAKPOINT_WIDESCREEN = 1280;

const MEDIA_MOBILE = `(max-width: ${BREAKPOINT_TABLET - 1}px)`;
const MEDIA_TABLET = `(min-width: ${BREAKPOINT_TABLET}px)`;
const MEDIA_DESKTOP = `(min-width: ${BREAKPOINT_DESKTOP}px)`;

const widthRanges = {
  MOBILE: [0, BREAKPOINT_TABLET - 1],
  TABLET: [BREAKPOINT_TABLET, BREAKPOINT_DESKTOP - 1],
  DESKTOP: [BREAKPOINT_DESKTOP, Infinity],
};

// eslint-disable-next-line no-restricted-globals
const isMediaSize = query => window.matchMedia(query).matches;

/**
 * Determines ViewportCategory by checking media query results, should always return
 * a ViewportCategory value that's in line with the responsive styles currently applied to the page.
 * @returns ViewportCategory
 */
const getViewportCategoryByMediaWidth = (): ViewportCategory => {
  let currentWidthType;

  if (isMediaSize(MEDIA_MOBILE)) {
    currentWidthType = 'MOBILE';
  } else if (isMediaSize(MEDIA_TABLET) && !isMediaSize(MEDIA_DESKTOP)) {
    currentWidthType = 'TABLET';
  } else {
    currentWidthType = 'DESKTOP';
  }

  return currentWidthType;
};

const getViewportCategory = (): ?ViewportCategory => {
  // eslint-disable-next-line no-restricted-globals
  if (typeof window === 'undefined' || !document || !document.documentElement) {
    return null;
  }

  /**
   * If possible, get ViewportCategory that aligns with current CSS media query results
   * rather than using clientWidth, since clientWidth is not what determines which
   * styles should be applied at any given time.
   */
  // eslint-disable-next-line no-restricted-globals
  if (window && window.matchMedia) {
    return getViewportCategoryByMediaWidth();
  }

  // eslint-disable-next-line no-restricted-globals
  const width = document.documentElement.clientWidth;
  let currentWidthType;
  const keys = Object.keys(widthRanges);

  // Find screen range that matches current width
  for (let i = 0; i < keys.length; i++) {
    const widthType = keys[i];
    const range = widthRanges[widthType];
    if (range[0] <= width && width <= range[1]) {
      currentWidthType = widthType;
      break;
    }
  }

  if (!currentWidthType) {
    return null;
  }

  return currentWidthType.toString();
};

export const getCategoryMinWidth = (cat: ?ViewportCategory | ?string) => {
  const range = cat && widthRanges[cat];
  return range ? range[0] : null;
};

// The resize listener will emit the viewport category to this radio after dispatching it to redux
export const RESPONSIVE_EVENTS_RADIO = '@ta/common.responsive';
export const RESPONSIVE_VIEWPORT_EVENT = 'viewportCategoryChange';

export default getViewportCategory;
